import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  createInvoice,
  deleteInvoice,
  uploadInvoicePdf,
  saveProjectInvoiceDraft,
  updateProject,
} from '../../services/expenseFirestore';
import { generateInvoicePdf } from '../../services/invoicePdfService';
import {
  buildProjectLineItems,
  rowTotal,
  computeInvoiceTotals,
} from '../../utils/invoiceUtils';
import { DEFAULT_SETTINGS } from '../../constants/expenseConstants';
import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';

const EMPTY_ROW = { particulars: '', price: '', qty: 1 };

function formatCurrency(val) {
  return `₹ ${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function generatePreviewInvoiceId(projectId) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  let formattedProjectId = (projectId || '').toUpperCase();
  const match = formattedProjectId.match(/^([A-Z]+)_?0*(\d+)$/);
  if (match) {
    formattedProjectId = match[1] + match[2].padStart(2, '0');
  } else {
    formattedProjectId = formattedProjectId.replace(/[^A-Z0-9]/g, '');
  }
  return `TVC${formattedProjectId}${year}${month}`;
}

export default function InvoiceModule({ projects, invoices, settings, loading }) {
  const companySettings = useMemo(() => ({ ...DEFAULT_SETTINGS, ...settings }), [settings]);

  const [additionalModal, setAdditionalModal] = useState(null);
  const [generateModal, setGenerateModal] = useState(null);
  const [editInvoiceModal, setEditInvoiceModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const [projectRows, setProjectRows] = useState([]);
  const [additionalRows, setAdditionalRows] = useState([{ ...EMPTY_ROW }]);
  
  const [poc, setPoc] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [gstNo, setGstNo] = useState('');
  
  const [generating, setGenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPrevious, setShowPrevious] = useState(true);

  const completedProjects = useMemo(
    () => projects.filter((p) => p.status === 'Completed'),
    [projects]
  );

  const pendingInvoiceProjects = useMemo(
    () => completedProjects.filter((p) => !invoices.some((i) => i.projectDocId === p.id)),
    [completedProjects, invoices]
  );

  const openAdditionalModal = (project) => {
    const draft = project.invoiceDraft || {};
    setAdditionalModal(project);
    setProjectRows(buildProjectLineItems(project));
    setAdditionalRows(
      draft.additionalRows?.length
        ? draft.additionalRows.map((r) => ({ ...r }))
        : [{ ...EMPTY_ROW }]
    );
    setPoc(draft.poc || '');
    setPurchaseOrder(draft.purchaseOrder || '');
    setGstNo(draft.gstNo || '');
  };

  const openGenerateModal = (project) => {
    const draft = project.invoiceDraft || {};
    setGenerateModal(project);
    setPoc(draft.poc || '');
    setPurchaseOrder(draft.purchaseOrder || '');
    setGstNo(draft.gstNo || '');
  };

  const openEditInvoiceModal = (invoice) => {
    const project = projects.find((p) => p.id === invoice.projectDocId);
    if (!project) {
      toast.error('Project data not found for this invoice.');
      return;
    }
    setEditInvoiceModal({ invoice, project });
    setProjectRows(buildProjectLineItems(project));
    setAdditionalRows(
      invoice.additionalRows?.length
        ? invoice.additionalRows.map((r) => ({ ...r }))
        : [{ ...EMPTY_ROW }]
    );
    setPoc(invoice.poc || '');
    setPurchaseOrder(invoice.purchaseOrder || '');
    setGstNo(invoice.gstNo || '');
  };

  const addRow = () => setAdditionalRows((r) => [...r, { ...EMPTY_ROW }]);
  const removeRow = (idx) => setAdditionalRows((r) => r.filter((_, i) => i !== idx));
  const updateRow = (idx, field, val) =>
    setAdditionalRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: val } : row)));

  const validAdditionalRows = additionalRows.filter((r) => r.particulars?.trim());
  const additionalTotal = validAdditionalRows.reduce((s, r) => s + rowTotal(r), 0);
  const projectBaseTotal = projectRows.reduce((s, r) => s + (r.total || 0), 0);

  const getPendingTotal = (project) => {
    const draft = project.invoiceDraft;
    return computeInvoiceTotals(project, draft?.additionalRows || [], companySettings).grandTotal;
  };

  const handleSaveAdditional = async () => {
    if (!additionalModal) return;
    setSavingDraft(true);
    try {
      const totals = computeInvoiceTotals(additionalModal, validAdditionalRows, companySettings);
      await saveProjectInvoiceDraft(additionalModal.id, {
        poc,
        purchaseOrder,
        gstNo,
        additionalRows: validAdditionalRows,
        additionalAmount: totals.additionalAmount,
        billedValue: totals.grandTotal,
      });
      toast.success('Additional amount saved. Billed value updated in Projects tab.');
      setAdditionalModal(null);
    } catch (e) {
      toast.error(e.message || 'Failed to save additional amount');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleGenerate = async (isRegenerate = false) => {
    const targetProject = isRegenerate ? editInvoiceModal?.project : generateModal;
    if (!targetProject) return;

    // Open a blank tab synchronously to prevent popup blockers
    const printWindow = window.open('about:blank', '_blank');

    setGenerating(true);
    try {
      let savedRows = [];
      let invoiceNumber = '';
      
      if (isRegenerate) {
        savedRows = validAdditionalRows;
        invoiceNumber = editInvoiceModal.invoice.invoiceId;
      } else {
        const draft = targetProject.invoiceDraft || {};
        savedRows = draft.additionalRows || [];
        invoiceNumber = generatePreviewInvoiceId(targetProject.projectId);
      }

      const client = {
        name: targetProject.client,
        state: targetProject.clientState || companySettings.companyState,
      };

      const { blob, subtotal, cgst, sgst, igst, grandTotal } = await generateInvoicePdf({
        invoiceNumber,
        project: targetProject,
        client,
        settings: companySettings,
        poc,
        purchaseOrder,
        gstNo,
        additionalRows: savedRows,
      });

      let pdfUrl = '';
      try {
        pdfUrl = await uploadInvoicePdf(blob, invoiceNumber);
      } catch (uploadError) {
        console.error('Failed to upload PDF to Firebase Storage:', uploadError);
        toast.warn('PDF could not be saved to cloud storage. Generating local download instead.');
      }

      const invoiceData = {
        invoiceId: invoiceNumber,
        projectDocId: targetProject.id,
        projectId: targetProject.projectId,
        projectName: targetProject.projectName,
        client: targetProject.client,
        subtotal,
        cgst,
        sgst,
        igst,
        grandTotal,
        additionalAmount: isRegenerate 
          ? computeInvoiceTotals(targetProject, savedRows, companySettings).additionalAmount
          : (targetProject.invoiceDraft?.additionalAmount || 0),
        additionalRows: savedRows,
        poc,
        purchaseOrder,
        gstNo,
        pdfUrl,
      };

      // Since createInvoice uses setDoc with invoiceId as doc ID, it will overwrite the existing one.
      await createInvoice(invoiceData);
      
      await updateProject(targetProject.id, {
        billedValue: grandTotal,
        projectName: targetProject.projectName,
      });

      toast.success(isRegenerate ? 'Invoice regenerated successfully' : 'Invoice generated successfully');
      
      if (isRegenerate) setEditInvoiceModal(null);
      else setGenerateModal(null);

      // Set the URL of the previously opened window
      if (printWindow) {
        const localUrl = URL.createObjectURL(blob);
        printWindow.location.href = localUrl;
        printWindow.focus();
      }
    } catch (e) {
      if (printWindow) printWindow.close();
      toast.error(e.message || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInvoice(deleteTarget.id);
      toast.success('Invoice deleted');
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const pendingColumns = [
    {
      key: 'invoiceId',
      label: 'Invoice ID',
      render: (r) => generatePreviewInvoiceId(r.projectId),
    },
    { key: 'projectId', label: 'Project ID' },
    { key: 'projectName', label: 'Project Name' },
    {
      key: 'total',
      label: 'Total',
      render: (r) => formatCurrency(getPendingTotal(r)),
    },
    {
      key: 'date',
      label: 'Date',
      render: (r) => r.completedDate || '—',
    },
    {
      key: 'additional',
      label: 'Additional Amount',
      render: (r) => (
        <button
          type="button"
          className="ui-btn ui-btn-secondary"
          style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
          onClick={() => openAdditionalModal(r)}
        >
          Additional Amount
        </button>
      ),
    },
    {
      key: 'generate',
      label: 'Generate Invoice',
      render: (r) => (
        <button
          type="button"
          className="btn-primary"
          style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
          onClick={() => openGenerateModal(r)}
        >
          <i className="fas fa-file-invoice" /> Generate Invoice
        </button>
      ),
    },
  ];

  const historyColumns = [
    { key: 'invoiceId', label: 'Invoice ID' },
    { key: 'projectId', label: 'Project ID' },
    { key: 'projectName', label: 'Project Name' },
    {
      key: 'grandTotal',
      label: 'Total',
      render: (r) => formatCurrency(r.grandTotal),
    },
    { key: 'date', label: 'Date' },
    {
      key: 'additionalAmount',
      label: 'Additional Amount',
      render: (r) => formatCurrency(r.additionalAmount),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="exp-actions">
          <button
            type="button"
            className="exp-icon-btn primary"
            onClick={() => openEditInvoiceModal(r)}
            title="Edit & Regenerate"
          >
            <i className="fas fa-edit" />
          </button>
          {r.pdfUrl && (
            <a
              href={r.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="exp-icon-btn"
              title="View PDF"
            >
              <i className="fas fa-file-pdf" />
            </a>
          )}
          <button
            type="button"
            className="exp-icon-btn danger"
            onClick={() => setDeleteTarget(r)}
            title="Delete"
          >
            <i className="fas fa-trash" />
          </button>
        </div>
      ),
    },
  ];

  const projectLineItemsTable = (
    <div className="exp-invoice-rows">
      <p className="exp-invoice-section-label">From Project (auto-fetched)</p>
      <div className="exp-invoice-row-header">
        <span>Particulars</span><span>Price</span><span>Qty</span><span>Total</span>
      </div>
      {projectRows.map((row, idx) => (
        <div key={idx} className="exp-invoice-row exp-invoice-row-readonly">
          <input readOnly value={row.particulars} />
          <input readOnly value={Number(row.price).toFixed(2)} />
          <input readOnly value={row.qty} />
          <input readOnly value={Number(row.total).toFixed(2)} />
        </div>
      ))}
      <div className="exp-invoice-total">Project Base Total: {formatCurrency(projectBaseTotal)}</div>
    </div>
  );

  const additionalLineItemsFields = (
    <div className="exp-invoice-rows">
      <p className="exp-invoice-section-label">Additional Line Items</p>
      <div className="exp-invoice-row-header">
        <span>Particulars</span><span>Price</span><span>Qty</span><span>Total</span><span></span>
      </div>
      {additionalRows.map((row, idx) => (
        <div key={idx} className="exp-invoice-row">
          <input
            placeholder="Particulars"
            value={row.particulars}
            onChange={(e) => updateRow(idx, 'particulars', e.target.value)}
          />
          <input
            type="number"
            placeholder="Price"
            value={row.price}
            onChange={(e) => updateRow(idx, 'price', e.target.value)}
          />
          <input
            type="number"
            placeholder="Qty"
            value={row.qty}
            onChange={(e) => updateRow(idx, 'qty', e.target.value)}
          />
          <input readOnly value={rowTotal(row).toFixed(2)} />
          <button
            type="button"
            className="exp-icon-btn danger"
            onClick={() => removeRow(idx)}
          >
            <i className="fas fa-trash" />
          </button>
        </div>
      ))}
      <button type="button" className="ui-btn ui-btn-secondary" onClick={addRow}>
        <i className="fas fa-plus" /> Add Row
      </button>
      <div className="exp-invoice-total">Additional Total: {formatCurrency(additionalTotal)}</div>
      {(additionalModal || editInvoiceModal) && (
        <div className="exp-invoice-gst-preview">
          {(() => {
            const project = additionalModal || editInvoiceModal?.project;
            const preview = computeInvoiceTotals(project, validAdditionalRows, companySettings);
            return preview.sameState ? (
              <span>
                GST (CGST @ {preview.gstRate / 2}% + SGST @ {preview.gstRate / 2}%):{' '}
                {formatCurrency(preview.totalTax)} — Grand Total: {formatCurrency(preview.grandTotal)}
              </span>
            ) : (
              <span>
                GST (IGST @ {preview.gstRate}%): {formatCurrency(preview.totalTax)} — Grand Total:{' '}
                {formatCurrency(preview.grandTotal)}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );

  const pocPoFields = (
    <div className="ui-form-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
      <div className="ui-form-group" style={{ flex: '1 1 30%' }}>
        <label>POC</label>
        <input value={poc} onChange={(e) => setPoc(e.target.value)} placeholder="Point of contact" />
      </div>
      <div className="ui-form-group" style={{ flex: '1 1 30%' }}>
        <label>Purchase Order</label>
        <input value={purchaseOrder} onChange={(e) => setPurchaseOrder(e.target.value)} placeholder="PO number" />
      </div>
      <div className="ui-form-group" style={{ flex: '1 1 30%' }}>
        <label>GST No</label>
        <input value={gstNo} onChange={(e) => setGstNo(e.target.value)} placeholder="Client GST No" />
      </div>
    </div>
  );

  return (
    <div className="expense-panel">
      <div className="expense-card-header">
        <h3 className="expense-panel-title">Invoice Generation</h3>
      </div>

      <h4 className="expense-section-heading">Generate New Invoices (Completed projects only)</h4>
      {loading ? (
        <p className="expense-loading">Loading…</p>
      ) : (
        <DataTable
          columns={pendingColumns}
          data={pendingInvoiceProjects}
          searchKeys={['projectId', 'projectName']}
          emptyMessage="All completed projects have invoices"
        />
      )}

      <div className="expense-card-header" style={{ marginTop: '2rem' }}>
        <h4 className="expense-section-heading" style={{ margin: 0 }}>Previous Invoices</h4>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setShowPrevious(!showPrevious)}>
          {showPrevious ? 'Hide' : 'Show'}
        </button>
      </div>
      {showPrevious && (
        <DataTable
          columns={historyColumns}
          data={invoices}
          searchKeys={['invoiceId', 'projectId', 'projectName']}
          emptyMessage="No invoices generated yet"
        />
      )}

      <Modal
        open={!!additionalModal}
        onClose={() => setAdditionalModal(null)}
        title="Additional Amount"
        size="lg"
        footer={
          <button
            type="button"
            className="ui-btn ui-btn-primary"
            onClick={handleSaveAdditional}
            disabled={savingDraft}
          >
            {savingDraft ? 'Saving…' : 'Save Configuration'}
          </button>
        }
      >
        {additionalModal && (
          <p style={{ marginTop: 0, color: '#6b7280' }}>
            Project: {additionalModal.projectName} ({additionalModal.projectId})
          </p>
        )}
        {pocPoFields}
        {projectLineItemsTable}
        {additionalLineItemsFields}
      </Modal>

      <Modal
        open={!!generateModal}
        onClose={() => setGenerateModal(null)}
        title="Generate Invoice"
        size="md"
        footer={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setGenerateModal(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="ui-btn ui-btn-primary"
              onClick={() => handleGenerate(false)}
              disabled={generating}
            >
              {generating ? 'Generating…' : 'Generate Invoice'}
            </button>
          </>
        }
      >
        {generateModal && (
          <>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Project: {generateModal.projectName} ({generateModal.projectId})
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              Total: {formatCurrency(getPendingTotal(generateModal))}
              {generateModal.invoiceDraft?.additionalAmount > 0 && (
                <span>
                  {' '}
                  (includes {formatCurrency(generateModal.invoiceDraft.additionalAmount)} additional amount)
                  <button
                    type="button"
                    className="ui-btn"
                    style={{ padding: '2px 8px', fontSize: '0.8rem', marginLeft: '8px' }}
                    onClick={() => {
                      const proj = generateModal;
                      setGenerateModal(null);
                      openAdditionalModal(proj);
                    }}
                  >
                    Edit
                  </button>
                </span>
              )}
            </p>
          </>
        )}
        {pocPoFields}
      </Modal>

      <Modal
        open={!!editInvoiceModal}
        onClose={() => setEditInvoiceModal(null)}
        title="Edit / Regenerate Invoice"
        size="lg"
        footer={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditInvoiceModal(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="ui-btn ui-btn-primary"
              onClick={() => handleGenerate(true)}
              disabled={generating}
            >
              {generating ? 'Regenerating…' : 'Regenerate Invoice'}
            </button>
          </>
        }
      >
        {editInvoiceModal && (
          <>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Invoice NO: {editInvoiceModal.invoice.invoiceId} <br/>
              Project: {editInvoiceModal.project.projectName} ({editInvoiceModal.project.projectId})
            </p>
            {pocPoFields}
            {projectLineItemsTable}
            {additionalLineItemsFields}
          </>
        )}
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Invoice"
        message={`Delete invoice ${deleteTarget?.invoiceId}?`}
        confirmLabel="Delete"
      />
    </div>
  );
}
